import React from 'react';
import { Table } from 'rsuite-table';
import type BaseFeuilleSaisie from '../../classes/feuilleSaisies/BaseFeuilleSaisie';
import type BaseTypeRegroupement from '../../classes/typeRegroupement/BaseTypeRegroupement';
import ROW_HEIGHT from '../../constants/feuilleSaisie/tableProps';
import TypePeriode from '../../constants/simulation/typePeriode';
import type TreeRow from '../../interfaces/feuilleSaisie/treeRow';
import type Simulation from '../../interfaces/simulation/simulation';
import renderMontantText from '../../utils/simulation/montant';
import { shouldAddReport } from '../../utils/simulation/simulation';
import 'rsuite-table/dist/css/rsuite-table.min.css';
import './TreeViewFooter.css';
import TypeBudget from '../../constants/simulation/typeBudget';
import type LigneMontantReference from '../../interfaces/simulation/ligneMontantReference';
import { getMontantSoldeRAR } from '../../utils/montantSimulation';

interface TreeViewFooterProps {
	simulation: Simulation;
	data: TreeRow;
	feuilleSaisie: BaseFeuilleSaisie;
	typeRegroupement: BaseTypeRegroupement;
	lignesMontantReferences: LigneMontantReference[];
}

const TreeViewFooter = ({
	simulation = null,
	data = null,
	feuilleSaisie = null,
	typeRegroupement = null,
	lignesMontantReferences = [],
}: TreeViewFooterProps) => {
	const colonneInfoCount =
		simulation?.budget?.type === TypeBudget.COMPTE_ADMINISTRATIF ? 3 : 2;

	return (
		<Table
			key="totaux"
			className="treeViewFooter"
			height={ROW_HEIGHT}
			rowHeight={ROW_HEIGHT}
			data={[typeRegroupement.getSolde(data)]}
			showHeader={false}
			rowKey="libelle"
		>
			{({ Cell, Column, HeaderCell }) => (
				<>
					<Column flexGrow={4} fullText verticalAlign="middle">
						<HeaderCell> </HeaderCell>
						<Cell dataKey="libelle" />
					</Column>

					{lignesMontantReferences
						?.slice(0, colonneInfoCount)
						?.map((column) => (
							<Column
								align="right"
								flexGrow={1}
								verticalAlign="middle"
								key={`total${column.typeMontant}`}
							>
								<HeaderCell> </HeaderCell>
								<Cell dataKey="budget">
									{(rowData) => renderMontantText(rowData[column.typeMontant])}
								</Cell>
							</Column>
						))}

					{feuilleSaisie.isCreditReporteShown &&
						shouldAddReport(simulation, feuilleSaisie) && (
							<Column align="right" flexGrow={1} verticalAlign="middle">
								<HeaderCell> </HeaderCell>
								<Cell dataKey="report">
									{(rowData) => renderMontantText(rowData.report, true)}
								</Cell>
							</Column>
						)}

					{simulation?.typePeriode === TypePeriode.DEMANDE && (
						<Column align="right" flexGrow={1} verticalAlign="middle">
							<HeaderCell> </HeaderCell>
							<Cell dataKey="montant_demande">
								{(rowData) => renderMontantText(rowData.montant_demande, true)}
							</Cell>
						</Column>
					)}

					{[TypePeriode.PROPOSE, TypePeriode.VOTE]?.includes(
						simulation?.typePeriode,
					) && (
						<Column align="right" flexGrow={1} verticalAlign="middle">
							<HeaderCell> </HeaderCell>
							<Cell dataKey="montant_propose">
								{(rowData) =>
									renderMontantText(
										rowData.montant_propose,
										simulation.typePeriode === TypePeriode.PROPOSE,
									)
								}
							</Cell>
						</Column>
					)}

					{simulation?.typePeriode === TypePeriode.VOTE && (
						<Column align="right" flexGrow={1} verticalAlign="middle">
							<HeaderCell> </HeaderCell>
							<Cell dataKey="montant_vote">
								{(rowData) => renderMontantText(rowData.montant_vote, true)}
							</Cell>
						</Column>
					)}

					{simulation?.typePeriode === TypePeriode.CA_CFU && (
						<Column align="right" flexGrow={1} verticalAlign="middle">
							<HeaderCell> </HeaderCell>
							<Cell dataKey="montantCaCfu">
								{(rowData) => renderMontantText(rowData.rar, true)}
							</Cell>
						</Column>
					)}

					{[
						TypeBudget.BUDGET_PRIMITIF,
						TypeBudget.BUDGET_SUPPLEMENTAIRE,
					]?.includes(simulation?.budget?.type) &&
						[TypePeriode.PROPOSE, TypePeriode.VOTE]?.includes(
							simulation?.typePeriode,
						) && (
							<Column align="right" flexGrow={1} verticalAlign="middle">
								<HeaderCell> </HeaderCell>
								<Cell dataKey="soldeRAR">
									{(rowData) =>
										renderMontantText(
											getMontantSoldeRAR(rowData, simulation),
											true,
										)
									}
								</Cell>
							</Column>
						)}

					<Column flexGrow={2}>
						<HeaderCell> </HeaderCell>
						<Cell />
					</Column>

					<Column flexGrow={1}>
						<HeaderCell> </HeaderCell>
						<Cell />
					</Column>
				</>
			)}
		</Table>
	);
};

export default TreeViewFooter;
